import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import {
  ImportCommitResponse,
  ImportEntityType,
  ImportValidationResponse,
  ResourceExportRecord,
  ResourceFullExportResponse,
  ResourceFullImportData,
  ResourceImportRecord,
  SorCodeExportRecord,
  SorCodeFullImportData,
  SorCodeImportRecord,
  SorCodeOverrideExportRecord,
  SorCodeOverrideImportRecord
} from "@app/models/dto/data-import.model";
import { ExcelService } from "@app/services/excel/excel.service";
import { ResourceStore } from "@app/services/store/resource-store";
import { SorCodeStore } from "@app/services/store/sor-code-store";
import { TeamStore } from "@app/services/store/team-store";
import { VisitTypeStore } from "@app/services/store/visit-type-store";
import { ClientConfigService } from "@app/core/config/client-config.service";

/**
 * Service for handling data import/export operations.
 * Coordinates between Excel parsing and backend validation/commit.
 * CR: ROCC-CR008745
 */
@Injectable({
  providedIn: "root"
})
export class DataImportService {
  private http = inject(HttpClient);
  private excelService = inject(ExcelService);
  private resourceStore = inject(ResourceStore);
  private sorCodeStore = inject(SorCodeStore);
  private teamStore = inject(TeamStore);
  private visitTypeStore = inject(VisitTypeStore);
  private clientConfigService = inject(ClientConfigService);

  /**
   * Temporarily holds parsed SOR code overrides between parseFile and validate/commit calls.
   * Populated when parsing a SOR_CODE Excel file with an Overrides sheet.
   */
  private pendingSorCodeOverrides: SorCodeOverrideImportRecord[] = [];

  private async getBaseUrl(): Promise<string> {
    const config = await this.clientConfigService.config;
    return `${config.serviceUrl}/api/v1/data-import`;
  }

  // ============================================
  // VALIDATION ENDPOINTS
  // ============================================

  /**
   * Validate resource import records against the backend
   */
  async validateResources(
    records: ResourceImportRecord[]
  ): Promise<ImportValidationResponse> {
    const baseUrl = await this.getBaseUrl();
    return firstValueFrom(
      this.http.post<ImportValidationResponse>(
        `${baseUrl}/resources/validate`,
        { records }
      )
    );
  }

  /**
   * Validate SOR code import records against the backend
   */
  async validateSorCodes(
    records: SorCodeImportRecord[],
    overrides?: SorCodeOverrideImportRecord[]
  ): Promise<ImportValidationResponse> {
    const baseUrl = await this.getBaseUrl();
    return firstValueFrom(
      this.http.post<ImportValidationResponse>(
        `${baseUrl}/sor-codes/validate`,
        { records, overrides: overrides || [] }
      )
    );
  }

  /**
   * Validate full resource import data including child records
   * (skills, zones, roster assignments, excluded properties)
   */
  async validateResourceFull(
    data: ResourceFullImportData
  ): Promise<ImportValidationResponse> {
    const baseUrl = await this.getBaseUrl();
    return firstValueFrom(
      this.http.post<ImportValidationResponse>(
        `${baseUrl}/resources/validate-full`,
        data
      )
    );
  }

  // ============================================
  // COMMIT ENDPOINTS
  // ============================================

  /**
   * Commit validated resource records
   */
  async commitResources(
    records: ResourceImportRecord[]
  ): Promise<ImportCommitResponse> {
    const baseUrl = await this.getBaseUrl();
    return firstValueFrom(
      this.http.post<ImportCommitResponse>(`${baseUrl}/resources/commit`, {
        records
      })
    );
  }

  /**
   * Commit validated SOR code records
   */
  async commitSorCodes(
    records: SorCodeImportRecord[],
    overrides?: SorCodeOverrideImportRecord[]
  ): Promise<ImportCommitResponse> {
    const baseUrl = await this.getBaseUrl();
    return firstValueFrom(
      this.http.post<ImportCommitResponse>(`${baseUrl}/sor-codes/commit`, {
        records,
        overrides: overrides || []
      })
    );
  }

  /**
   * Commit full resource import data including child records
   * (skills, zones, roster assignments, excluded properties)
   */
  async commitResourceFull(
    data: ResourceFullImportData
  ): Promise<ImportCommitResponse> {
    const baseUrl = await this.getBaseUrl();
    return firstValueFrom(
      this.http.post<ImportCommitResponse>(
        `${baseUrl}/resources/commit-full`,
        data
      )
    );
  }

  // ============================================
  // EXPORT OPERATIONS
  // ============================================

  /**
   * Export all resources to Excel with full data including skills, zones, roster, and excluded properties.
   * Creates a multi-sheet workbook with reference data.
   */
  async exportResourcesToExcel(): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    // Fetch full export data from backend (includes all related data)
    const exportData = await firstValueFrom(
      this.http.get<ResourceFullExportResponse>(
        `${baseUrl}/resources/export-full`
      )
    );

    // Generate multi-sheet Excel with all data
    this.excelService.generateResourceFullExcel(exportData);
  }

  /**
   * Download empty resources template with reference data
   */
  async downloadResourceTemplate(): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    // Fetch full export to get reference data
    const exportData = await firstValueFrom(
      this.http.get<ResourceFullExportResponse>(
        `${baseUrl}/resources/export-full`
      )
    );

    // Generate template with reference data but empty data sheets
    this.excelService.generateResourceTemplate({
      teams: exportData.validTeams,
      skills: exportData.validSkills,
      zones: exportData.validZones,
      divisions: exportData.validDivisions,
      mappingPatterns: exportData.validMappingPatterns
    });
  }

  /**
   * Export all SOR codes to Excel with reference data and overrides
   */
  async exportSorCodesToExcel(): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    // Fetch export data from backend (includes visit type descriptions)
    const exportRecords = await firstValueFrom(
      this.http.get<SorCodeExportRecord[]>(`${baseUrl}/sor-codes/export`)
    );

    // Fetch override export data
    const overrideRecords = await firstValueFrom(
      this.http.get<SorCodeOverrideExportRecord[]>(
        `${baseUrl}/sor-codes/overrides/export`
      )
    );

    // Load reference data for the template
    const visitTypesPage = await this.visitTypeStore.loadAll(null);
    const visitTypes = visitTypesPage.resources.map((vt) => vt.description);

    // Generate Excel with reference data and overrides
    this.excelService.generateSorCodeExcel(
      exportRecords,
      { visitTypes },
      overrideRecords
    );
  }

  /**
   * Download empty SOR codes template with reference data
   */
  async downloadSorCodeTemplate(): Promise<void> {
    // Load reference data
    const visitTypesPage = await this.visitTypeStore.loadAll(null);
    const visitTypes = visitTypesPage.resources.map((vt) => vt.description);

    this.excelService.generateSorCodeTemplate({ visitTypes });
  }

  // ============================================
  // IMPORT OPERATIONS (FULL FLOW)
  // ============================================

  /**
   * Parse an Excel file and return records.
   * For SOR_CODE imports, also parses the Overrides sheet and stores them internally.
   */
  async parseFile(
    file: File,
    entityType: ImportEntityType
  ): Promise<ResourceImportRecord[] | SorCodeImportRecord[]> {
    if (!this.excelService.isValidExcelFile(file)) {
      throw new Error(
        "Invalid file type. Please upload an Excel file (.xlsx or .xls)"
      );
    }

    if (entityType === "SOR_CODE") {
      const fullData = await this.excelService.parseSorCodeExcelFull(file);
      this.pendingSorCodeOverrides = fullData.overrides || [];
      return fullData.records;
    }

    return this.excelService.parseExcelFile(file, entityType);
  }

  /**
   * Validate parsed records
   */
  async validate(
    records: ResourceImportRecord[] | SorCodeImportRecord[],
    entityType: ImportEntityType
  ): Promise<ImportValidationResponse> {
    if (entityType === "RESOURCE") {
      return this.validateResources(records as ResourceImportRecord[]);
    } else {
      return this.validateSorCodes(
        records as SorCodeImportRecord[],
        this.pendingSorCodeOverrides
      );
    }
  }

  /**
   * Commit validated records
   */
  async commit(
    records: ResourceImportRecord[] | SorCodeImportRecord[],
    entityType: ImportEntityType
  ): Promise<ImportCommitResponse> {
    if (entityType === "RESOURCE") {
      return this.commitResources(records as ResourceImportRecord[]);
    } else {
      const result = await this.commitSorCodes(
        records as SorCodeImportRecord[],
        this.pendingSorCodeOverrides
      );
      this.pendingSorCodeOverrides = []; // Clear after commit
      return result;
    }
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  /**
   * Check if current time is within core business hours (8am - 6pm)
   */
  isWithinCoreHours(): boolean {
    const now = new Date();
    const hour = now.getHours();
    return hour >= 8 && hour < 18;
  }

  /**
   * Get current time formatted for display
   */
  getCurrentTimeFormatted(): string {
    return new Date().toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit"
    });
  }
}
